export const YEAR_OPTIONS = ['2026', '2025', '2024', '2023', '2022', '2021'];

export const HALF_YEAR_OPTIONS = [
  { value: 'full_year', label: '全年' },
  { value: 'first_half', label: '上半年' },
  { value: 'second_half', label: '下半年' },
];

export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}月`,
}));

export const PROJECT_TYPE_OPTIONS = [
  { value: 'wind', label: '风电' },
  { value: 'centralized_pv', label: '集中式光伏' },
  { value: 'commercial_pv', label: '工商业光伏' },
  { value: 'residential_pv', label: '户用光伏' },
  { value: 'biomass', label: '生物质' },
  { value: 'hydro', label: '水电' },
  { value: 'other', label: '其他' },
];

export const CHANNEL_OPTIONS = [
  { value: 'non_channel', label: '非通道' },
  { value: 'channel', label: '通道' },
];

export const PROVINCE_OPTIONS = [
  { value: 'no_requirement', label: '无要求' },
  { value: 'beijing', label: '北京' },
  { value: 'tianjin', label: '天津' },
  { value: 'hebei', label: '河北' },
  { value: 'shanxi', label: '山西' },
  { value: 'neimenggu', label: '内蒙古' },
  { value: 'liaoning', label: '辽宁' },
  { value: 'jilin', label: '吉林' },
  { value: 'heilongjiang', label: '黑龙江' },
  { value: 'shanghai', label: '上海' },
  { value: 'jiangsu', label: '江苏' },
  { value: 'zhejiang', label: '浙江' },
  { value: 'anhui', label: '安徽' },
  { value: 'fujian', label: '福建' },
  { value: 'jiangxi', label: '江西' },
  { value: 'shandong', label: '山东' },
  { value: 'henan', label: '河南' },
  { value: 'hubei', label: '湖北' },
  { value: 'hunan', label: '湖南' },
  { value: 'guangdong', label: '广东' },
  { value: 'guangxi', label: '广西' },
  { value: 'hainan', label: '海南' },
  { value: 'chongqing', label: '重庆' },
  { value: 'sichuan', label: '四川' },
  { value: 'guizhou', label: '贵州' },
  { value: 'yunnan', label: '云南' },
  { value: 'xizang', label: '西藏' },
  { value: 'shaanxi', label: '陕西' },
  { value: 'gansu', label: '甘肃' },
  { value: 'qinghai', label: '青海' },
  { value: 'ningxia', label: '宁夏' },
  { value: 'xinjiang', label: '新疆' },
  { value: 'hongkong', label: '香港' },
  { value: 'macao', label: '澳门' },
  { value: 'taiwan', label: '台湾' },
];

export const SUBSIDY_OPTIONS = [
  { value: 'non_subsidized', label: '非补贴' },
  { value: 'subsidized', label: '补贴' },
  { value: 'pending', label: '待纳入' },
];

export const TRADE_METHOD_OPTIONS = [
  { value: 'transfer', label: '划转' },
  { value: 'certify', label: '制证' },
];

export const EXCHANGE_OPTIONS = [
  { value: 'green_cert_platform', label: '中国绿证平台' },
  { value: 'guangzhou', label: '广州' },
  { value: 'beijing', label: '北京' },
  { value: 'inner_mongolia', label: '内蒙' },
];

export const OFFER_BY_OPTIONS = [
  { value: 'counterparty', label: '需要对方出价' },
];

export const TRADE_PRINCIPLE_OPTIONS = [
  { value: 'price_priority', label: '价格优先' },
  { value: 'time_priority', label: '时间优先' },
];

export interface GreenCertFormData {
  direction: 'buy' | 'sell';
  year: string;
  halfYear: string[];
  month: string[];
  projectType: string[];
  channel: string[];
  provinceRequirement: string[];
  subsidy: string[];
  quantity: string;
  price: string;
  tradeMethod: string[];
  exchange: string[];
  offerBy: string[];
  tradePrinciple: string[];
  expiryDate: string;
  otherInfo: string;
}

export const initialForm: GreenCertFormData = {
  direction: 'sell',
  year: '',
  halfYear: [],
  month: [],
  projectType: [],
  channel: [],
  // 省份要求默认不选
  provinceRequirement: [],
  subsidy: [],
  quantity: '',
  price: '',
  tradeMethod: [],
  exchange: [],
  offerBy: [],
  tradePrinciple: [],
  expiryDate: '',
  otherInfo: '',
};

export type GreenCertField =
  | 'direction'
  | 'year'
  | 'halfYear'
  | 'month'
  | 'projectType'
  | 'channel'
  | 'provinceRequirement'
  | 'subsidy'
  | 'quantity'
  | 'price'
  | 'tradeMethod'
  | 'exchange'
  | 'offerBy'
  | 'tradePrinciple'
  | 'expiryDate'
  | 'otherInfo';

// ---- Value-to-Label Helpers ----

function mapLabel(arr: { value: string; label: string }[], val: string): string {
  return arr.find((o) => o.value === val)?.label ?? val;
}

function mapLabels(arr: { value: string; label: string }[], vals: string[]): string {
  return vals.map((v) => mapLabel(arr, v)).join('、');
}

export const fieldLabels: Record<GreenCertField, string> = {
  direction: '交易方向',
  year: '年份',
  halfYear: '半年',
  month: '月份',
  projectType: '项目类型',
  channel: '通道与否',
  provinceRequirement: '省份要求',
  subsidy: '补贴与否',
  quantity: '数量（张）',
  price: '价格（元/个）',
  tradeMethod: '交易方式',
  exchange: '交易所',
  offerBy: '出价方',
  tradePrinciple: '交易原则',
  expiryDate: '信息有效期',
  otherInfo: '其他信息',
};

export function formatFieldValue(field: GreenCertField, value: string | string[]): string {
  if (Array.isArray(value)) {
    switch (field) {
      case 'halfYear':
        return mapLabels(HALF_YEAR_OPTIONS, value);
      case 'month':
        return mapLabels(MONTH_OPTIONS, value);
      case 'projectType':
        return mapLabels(PROJECT_TYPE_OPTIONS, value);
      case 'channel':
        return mapLabels(CHANNEL_OPTIONS, value);
      case 'provinceRequirement':
        return mapLabels(PROVINCE_OPTIONS, value);
      case 'subsidy':
        return mapLabels(SUBSIDY_OPTIONS, value);
      case 'tradeMethod':
        return mapLabels(TRADE_METHOD_OPTIONS, value);
      case 'exchange':
        return mapLabels(EXCHANGE_OPTIONS, value);
      case 'offerBy':
        return mapLabels(OFFER_BY_OPTIONS, value);
      case 'tradePrinciple':
        return mapLabels(TRADE_PRINCIPLE_OPTIONS, value);
      default:
        return value.join('、');
    }
  }
  if (field === 'direction') {
    return value === 'buy' ? '买入' : '卖出';
  }
  return value;
}